import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/Button';
import { extractDocumentWithRetry } from '../lib/opencv-document-scanner-react';

interface DocExtractFromImageProps {
  originalImage: File;
  onExtracted: (extractedFile: File) => void;
  onCancel: () => void;
  onRetryCapture?: () => void;
}

export function DocExtractFromImage({
  originalImage,
  onExtracted,
  onCancel,
  onRetryCapture
}: DocExtractFromImageProps) {
  const [originalImageUrl, setOriginalImageUrl] = useState<string>('');
  const [extractedImageUrl, setExtractedImageUrl] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string>('');
  const [useExtracted, setUseExtracted] = useState(false);
  const [extractionAttempts, setExtractionAttempts] = useState(0);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    // Créer l'URL pour l'image originale
    const url = URL.createObjectURL(originalImage);
    setOriginalImageUrl(url);

    return () => {
      URL.revokeObjectURL(url);
      if (extractedImageUrl) {
        URL.revokeObjectURL(extractedImageUrl);
      }
    };
  }, [originalImage]);

  const handleExtractDocument = async () => {
    if (!imgRef.current) return;

    setIsProcessing(true);
    setError('');
    setExtractionAttempts(prev => prev + 1);

    try {
      // Attendre que l'image soit chargée
      await new Promise((resolve) => {
        if (imgRef.current?.complete) {
          resolve(true);
        } else {
          imgRef.current?.addEventListener('load', resolve);
        }
      });

      console.log('🔍 [DOCEXTRACT] Début extraction, tentative:', extractionAttempts + 1);
      
      // Utiliser la fonction d'extraction robuste avec plusieurs tentatives
      const extractedCanvas = await extractDocumentWithRetry(imgRef.current);
      
      if (extractedCanvas) {
        console.log('✅ [DOCEXTRACT] Document extrait avec succès');
        
        // Convertir en blob
        extractedCanvas.toBlob((blob) => {
          if (blob) {
            const extractedFile = new File([blob], `extracted-${originalImage.name}`, {
              type: 'image/jpeg'
            });
            const extractedUrl = URL.createObjectURL(blob);
            setExtractedImageUrl(extractedUrl);
            setUseExtracted(true);
            setError('');
          } else {
            setError('Impossible de traiter l\'image extraite');
          }
          setIsProcessing(false);
        }, 'image/jpeg', 0.9);
      } else {
        console.warn('⚠️ [DOCEXTRACT] Aucun document détecté');
        setError('Aucun document détecté dans l\'image. Essayez de repositionner le document ou utilisez l\'image originale.');
        setUseExtracted(false);
        setIsProcessing(false);
      }
    } catch (err) {
      console.error('❌ [DOCEXTRACT] Erreur extraction:', err);
      setError('Erreur lors de l\'extraction du document. Essayez à nouveau.');
      setUseExtracted(false);
      setIsProcessing(false);
    }
  };

  const handleRetryCapture = () => {
    if (onRetryCapture) {
      onRetryCapture();
    }
  };

  const handleUseOriginal = () => {
    console.log('✅ [DOCEXTRACT] Utilisation de l\'image originale');
    onExtracted(originalImage);
  };

  const handleUseExtracted = () => {
    console.log('✅ [DOCEXTRACT] Utilisation de l\'image extraite');
    if (extractedImageUrl) {
      fetch(extractedImageUrl)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], `extracted-${originalImage.name}`, {
            type: 'image/jpeg'
          });
          onExtracted(file);
        });
    }
  };

  const handleConfirm = () => {
    if (useExtracted && extractedImageUrl) {
      handleUseExtracted();
    } else {
      handleUseOriginal();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[95vh] overflow-hidden mx-2 sm:mx-0">
        <div className="p-3 sm:p-4 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
            Extraction du document
          </h2>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">
            Validez l'image extraite ou utilisez l'originale
          </p>
        </div>

        <div className="p-3 sm:p-6 overflow-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {/* Image originale */}
            <div className="space-y-2 sm:space-y-3">
              <h3 className="font-medium text-gray-700 text-sm sm:text-base">Image originale</h3>
              <div className="border border-gray-300 rounded-lg p-2 bg-gray-50">
                <img
                  ref={imgRef}
                  src={originalImageUrl}
                  alt="Image originale"
                  className="w-full h-auto max-h-48 sm:max-h-64 object-contain"
                />
              </div>
              <div className="flex justify-center">
                <Button
                  type="button"
                  variant={!useExtracted ? "primary" : "outline"}
                  onClick={handleUseOriginal}
                  disabled={isProcessing}
                  size="sm"
                  className="text-xs sm:text-sm"
                >
                  Utiliser l'originale
                </Button>
              </div>
            </div>

            {/* Image extraite */}
            <div className="space-y-2 sm:space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-700 text-sm sm:text-base">Document extrait</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleExtractDocument}
                  disabled={isProcessing}
                  className="text-xs sm:text-sm"
                >
                  {isProcessing ? 'Extraction...' : 'Extraire'}
                </Button>
              </div>
              
              <div className="border border-gray-300 rounded-lg p-2 bg-gray-50 min-h-48 sm:min-h-64 flex items-center justify-center">
                {extractedImageUrl ? (
                  <img
                    src={extractedImageUrl}
                    alt="Document extrait"
                    className="w-full h-auto max-h-48 sm:max-h-64 object-contain"
                  />
                ) : (
                  <div className="text-gray-500 text-center py-8 sm:py-16">
                    {isProcessing ? (
                      <div className="flex flex-col items-center space-y-2">
                        <div className="w-6 h-6 sm:w-8 sm:h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                        <span className="text-xs sm:text-sm">Extraction en cours...</span>
                      </div>
                    ) : (
                      <div className="px-2">
                        <svg className="w-8 h-8 sm:w-12 sm:h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                        </svg>
                        <p className="mt-2 text-xs sm:text-sm">Cliquez sur "Extraire" pour détecter le document</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="flex justify-center">
                <Button
                  type="button"
                  variant={useExtracted ? "primary" : "outline"}
                  onClick={handleUseExtracted}
                  disabled={isProcessing || !extractedImageUrl}
                  size="sm"
                  className="text-xs sm:text-sm"
                >
                  Utiliser l'extrait
                </Button>
              </div>
            </div>
          </div>

          {/* Message d'erreur */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-700 text-xs sm:text-sm">{error}</p>
            </div>
          )}
        </div>

        <div className="p-3 sm:p-4 border-t border-gray-200 flex flex-col sm:flex-row justify-between gap-3 sm:gap-0">
          <div className="flex justify-center sm:justify-start">
            {onRetryCapture && (
              <Button
                type="button"
                variant="outline"
                onClick={handleRetryCapture}
                disabled={isProcessing}
                size="sm"
                className="text-xs sm:text-sm w-full sm:w-auto"
              >
                📸 Nouvelle capture
              </Button>
            )}
          </div>
          <div className="flex justify-center sm:justify-end space-x-2 sm:space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isProcessing}
              size="sm"
              className="text-xs sm:text-sm"
            >
              Annuler
            </Button>
            <Button
              type="button"
              onClick={handleConfirm}
              disabled={isProcessing}
              size="sm"
              className="text-xs sm:text-sm"
            >
              Confirmer
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}